/**
 * W-01 (FIX-WALK w01) - "התראה חדשה" from the bell. Pure helpers (unit-tested in
 * utils/__tests__/reminderModel.test.ts): the form's state and its client-side checks (the server checks
 * again, and alone decides who may be chosen), and which bell rows pop up as a toast.
 *
 * Dates and times are Israeli local ('YYYY-MM-DD' / 'HH:MM') and are sent as typed; the server turns them
 * into an instant in Asia/Jerusalem, so a browser in another time zone does not move the reminder.
 */
import type { Notification } from '../types';

export const REMINDER_EVENT = 'custom_reminder';
export const REPOP_CHOICES = [5, 10, 15, 30, 60] as const;
export type RepeatKind = 'once' | 'daily' | 'weekly';
/** 0 = Sunday ... 6 = Saturday (Postgres dow). */
export const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

export interface ReminderForm {
  message: string;
  userId: string;
  date: string;
  time: string;
  repeat: RepeatKind;
  weekdays: number[];
  until: string;
  repopMinutes: number | null;
  keepUntilDone: boolean;
}

export interface ReminderRow {
  id: string;
  created_by: string;
  user_id: string;
  message: string;
  start_date: string;
  start_time: string;
  repeat_kind: RepeatKind;
  weekdays: number[] | null;
  until_date: string | null;
  repop_minutes: number | null;
  keep_until_done: boolean;
  active: boolean;
  recipient_name?: string | null;
  creator_name?: string | null;
  last_fired_at?: string | null;
}

/** Wall-clock parts of an instant in Israel (the reminder's own clock), whatever the browser's zone. */
function israelParts(d: Date): { date: string; hour: string; dow: number } {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23', weekday: 'short' })
      .formatToParts(d).map((x) => [x.type, x.value]),
  ) as Record<string, string>;
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday);
  return { date: `${p.year}-${p.month}-${p.day}`, hour: p.hour, dow };
}

export function emptyReminderForm(userId: string, now: Date = new Date()): ReminderForm {
  const soon = israelParts(new Date(now.getTime() + 60 * 60 * 1000));
  return {
    message: '',
    userId,
    date: soon.date,
    time: `${soon.hour}:00`,
    repeat: 'once',
    weekdays: [soon.dow],
    until: '',
    repopMinutes: null,
    keepUntilDone: false,
  };
}

export function formFromRow(r: ReminderRow): ReminderForm {
  return {
    message: r.message,
    userId: r.user_id,
    date: r.start_date,
    time: r.start_time,
    repeat: r.repeat_kind,
    weekdays: r.weekdays ?? [],
    until: r.until_date ?? '',
    repopMinutes: r.repop_minutes,
    keepUntilDone: r.keep_until_done,
  };
}

export type ReminderFormError = 'message' | 'date' | 'time' | 'weekdays' | 'until' | 'keep';

/** The first problem, or null. (A past time is refused by the server, which knows Israeli time.) */
export function reminderFormError(f: ReminderForm): ReminderFormError | null {
  if (!f.message.trim()) return 'message';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.date)) return 'date';
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(f.time)) return 'time';
  if (f.repeat === 'weekly' && f.weekdays.length === 0) return 'weekdays';
  if (f.repeat !== 'once' && f.until && f.until < f.date) return 'until';
  if (f.keepUntilDone && f.repopMinutes === null) return 'keep';
  return null;
}

/** The request body. A repeat that is not chosen sends no weekdays / end date. */
export function reminderPayload(f: ReminderForm, selfId: string) {
  return {
    ...(f.userId && f.userId !== selfId ? { user_id: f.userId } : {}),
    message: f.message.trim(),
    start_date: f.date,
    start_time: f.time,
    repeat_kind: f.repeat,
    weekdays: f.repeat === 'weekly' ? [...f.weekdays].sort((a, b) => a - b) : null,
    until_date: f.repeat !== 'once' && f.until ? f.until : null,
    repop_minutes: f.repopMinutes,
    keep_until_done: f.repopMinutes !== null && f.keepUntilDone,
  };
}

export function isReminder(n: Pick<Notification, 'event_type'>): boolean {
  return n.event_type === REMINDER_EVENT;
}

/** One pop-up per (row, firing): the same row popping again after "every X minutes" has a new key. */
export function reminderPopKey(n: Pick<Notification, 'id' | 'last_occurred_at' | 'occurrence_count'>): string {
  return `${n.id}:${n.last_occurred_at ?? ''}:${n.occurrence_count ?? 1}`;
}

/**
 * The unread reminder rows not shown yet in this session. Unlike a ticket arrival, the FIRST poll
 * counts too: a reminder that fired while the person was away waits for them at the next login.
 */
export function newReminderPops(list: Notification[], shown: Set<string>, limit = 3): Notification[] {
  const out: Notification[] = [];
  for (const n of list) {
    if (!isReminder(n) || n.read_at) continue;
    const key = reminderPopKey(n);
    if (shown.has(key)) continue;
    if (out.length >= limit) break; // not shown now: stays unmarked, the next poll shows it
    shown.add(key);
    out.push(n);
  }
  return out;
}
